import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Unity, useUnityContext } from "react-unity-webgl";
import useRefreshToken from "../../../hooks/useRefreshToken"
import { clearToken } from "../../../auth/tokenStore";
import { logout } from "../../../slice/authSlice";
import { axiosPrivate } from "../../../api/axios";

function LEPage() {
  useEffect(() => {
    document.title = "레이아웃 편집";
  }, []);
  const [projectId, setProjectId] = useState(null);
  const { group_code, project_code } = useParams();
  const { unityProvider, unload, sendMessage, isLoaded, loadingProgression } = useUnityContext({
    loaderUrl: "https://preview.insightwin.com/BuildLE/WebLE.loader.js",
    dataUrl: "https://preview.insightwin.com/BuildLE/WebLE.data.gz",
    frameworkUrl: "https://preview.insightwin.com/BuildLE/WebLE.framework.js.gz",
    codeUrl: "https://preview.insightwin.com/BuildLE/WebLE.wasm.gz",
    streamingAssetsUrl: `https://preview.insightwin.com/StreamingAssets/${group_code}/${project_code}/le`,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const unityContainerRef = useRef(null);
  const [devicePixelRatio, setDevicePixelRatio] = useState(window.devicePixelRatio);

  // Tailwind CDN Dynamic Loading (Scoped to this page lifecycle)
  const [isTailwindReady, setIsTailwindReady] = useState(false);
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com";
    script.id = "tailwind-cdn-script";
    script.onload = () => {
      // Small timeout to ensure Tailwind has processed the DOM
      setTimeout(() => setIsTailwindReady(true), 100);
    };
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById("tailwind-cdn-script");
      if (existingScript) existingScript.remove();

      const tailwindStyles = document.querySelectorAll('style');
      tailwindStyles.forEach(style => {
        if (style.textContent.includes('--tw-') || style.textContent.includes('tailwindcss')) {
          style.remove();
        }
      });
    };
  }, []);

  // Refresh Token 갱신
  const refresh = useRefreshToken();

  // 토큰 갱신 인터벌 설정
  useEffect(() => {
    let refreshInterval;

    const setupRefreshInterval = () => {
      if (refreshInterval) {
        console.log("Clearing existing interval");
        clearInterval(refreshInterval);
      }

      refreshInterval = setInterval(async () => {
        try {
          console.log("토큰 갱신중");
          await refresh(); // Refresh Token 갱신
          console.log("토큰 갱신 완료");
        } catch (error) {
          console.error("Failed to refresh tokens:", error);
          clearToken();
          dispatch(logout());
        }
      }, 12 * 60 * 60 * 1000); // 12시간마다 갱신
    };

    setupRefreshInterval();

    return () => {
      console.log("Clearing interval on cleanup");
      clearInterval(refreshInterval); // 컴포넌트 언마운트 시 정리
    };
  }, [refresh, dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const pid = queryParams.get("pid");

    // pid가 없거나 null인 경우 처리
    if (!pid) {
      console.warn("프로젝트 ID가 없습니다. URL 확인이 필요합니다.");
      // 필요한 경우 기본값 설정 또는 에러 처리
      // 예: 이전에 사용한 프로젝트 ID가 있으면 사용
      const lastUsedPid = localStorage.getItem("lastUsedProjectId");
      if (lastUsedPid) {
        setProjectId(lastUsedPid);
      } else {
        // 에러 상태 표시 또는 다른 페이지로 리다이렉트
        alert("프로젝트 ID가 없습니다. URL 확인이 필요합니다.");
        return;
      }
    }

    setProjectId(pid);
    // 나중에 사용할 수 있도록 저장
    localStorage.setItem("lastUsedProjectId", pid);
  }, [location.search]);

  // 텍스쳐 파일 업로드 처리
  const OpenFileDialog = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".jpg,.jpeg,image/jpeg"; // 더 정확한 MIME 타입 지정

    input.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) {
        console.log("파일이 선택되지 않았습니다.");
        return;
      }

      // 파일 크기 검증 (3MB = 3 * 1024 * 1024 bytes)
      const maxSize = 3 * 1024 * 1024;
      if (file.size > maxSize) {
        alert("파일 크기는 3MB를 초과할 수 없습니다.");
        return;
      }

      // 파일 확장자 검증
      const allowedExtensions = ['jpg', 'jpeg'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        alert("JPG 파일만 업로드 가능합니다.");
        return;
      }

      const fileName = file.name.split(".")[0];
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const base64 = event.target.result.split(',')[1];
          const dataToSend = fileName + "|" + base64;
          sendMessage("img_TexturePicker", "ReceiveFileData", dataToSend);
        } catch (error) {
          console.error("파일 처리 중 오류 발생:", error);
          alert("파일 처리 중 오류가 발생했습니다.");
        }
      };

      reader.onerror = () => {
        console.error("파일 읽기 실패");
        alert("파일을 읽을 수 없습니다.");
      };

      reader.readAsDataURL(file);
    };

    input.click();
  }, [sendMessage]);

  // Unity에서 호출할 파일 업로드 함수
  const Upload_Image_To_StreamingAssets = useCallback(async (base64Data, fileName) => {
    try {
      // Base64를 바이너리로 변환
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Blob 생성 (실제 이미지 데이터 포함)
      const blob = new Blob([bytes], { type: 'image/jpeg' });

      // FormData 생성
      const formData = new FormData();
      formData.append('files', blob, `${fileName}.jpg`);
      formData.append('project_id', projectId);
      formData.append('group_code', group_code);
      formData.append('project_code', project_code);
      formData.append('upload_type', 'texture');

      // 백엔드 API 호출
      const uploadResponse = await axiosPrivate.post("/file/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: { overwrite: false },
      });

      console.log("파일 업로드 성공:", uploadResponse.data);

      // Unity에 성공 결과 전송
      sendMessage("img_TexturePicker", "OnUploadSuccess", uploadResponse.data.filePath || "success");

    } catch (error) {
      console.error("파일 업로드 실패:", error);

      // Unity에 실패 결과 전송
      sendMessage("img_TexturePicker", "OnUploadError", error.message || "Upload failed");
    }
  }, [projectId, group_code, project_code, sendMessage]);

  // 유니티에서 호출할 텍스쳐 삭제 함수
  const Delete_Image_To_StreamingAssets = useCallback(async (fileName) => {
    try {
      const textureFileName = `${fileName}.jpg`;
      const deleteResponse = await axiosPrivate.delete("/file/delete", {
        params: { project_id: projectId, group_code, project_code, file_name: textureFileName, upload_type: 'texture' },
      });

      console.log("파일 삭제 성공:", deleteResponse.data);

      // Unity에 성공 결과 전송
      sendMessage("img_TexturePicker", "OnDeleteSuccess", fileName);
    } catch (error) {
      console.error("파일 삭제 실패:", error);

      // Unity에 실패 결과 전송
      sendMessage("img_TexturePicker", "OnDeleteError", error.message || "Delete failed");
    }
  }, [projectId, group_code, project_code, sendMessage]);


  // 모델(OBJ/MTL) 업로드: 파일 선택 → 서버 업로드 → URL Unity 전달
  const OpenModelFileDialog = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".obj,.mtl,model/obj,text/plain,.jpg,.jpeg,.png"; // 일부 브라우저 mtl mime이 text/plain로 잡힐 수 있음
    input.multiple = true;

    input.onchange = async (event) => {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) {
        console.log("모델 파일이 선택되지 않았습니다.");
        return;
      }

      // 검증: 확장자/크기(100MB)
      const allowed = ["obj", "mtl", "jpg", "jpeg", "png"];
      const maxSize = 100 * 1024 * 1024; // 100MB
      const invalid = files.find((f) => {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        return !allowed.includes(ext) || f.size > maxSize;
      });
      if (invalid) {
        alert("OBJ/MTL/JPG/JPEG만 가능하며, 파일 크기는 최대 100MB까지 허용됩니다.");
        return;
      }

      // 업로드
      try {
        const form = new FormData();
        files.forEach((f) => form.append("files", f, f.name));
        form.append("project_id", projectId);
        form.append("group_code", group_code);
        form.append("project_code", project_code);
        form.append("upload_type", "model");

        const resp = await axiosPrivate.post("/file/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          params: { overwrite: true },
        });

        const uploadedNames = (resp?.data?.uploadedFiles || []).map(
          (n) => (typeof n === "string" ? n : String(n))
        );

        // 서버가 fileUrls를 내려주면 우선 사용, 없으면 경로 생성
        let urls = resp?.data?.fileUrls;
        if (!Array.isArray(urls) || urls.length === 0) {
          const origin = window.location.origin;
          urls = uploadedNames.map(
            (name) =>
              `${origin}/StreamingAssets/${group_code}/${project_code}/le/Data/${encodeURIComponent(
                name
              )}`
          );
        }

        // Unity로 URL 목록 전달 (파이프 구분)
        const payload = urls.join("|");
        sendMessage("Canvas", "OnModelUploadSuccess", payload);
      } catch (err) {
        console.error("모델 파일 업로드 실패:", err);
        sendMessage(
          "Canvas",
          "OnModelUploadError",
          err?.message || "Upload failed"
        );
      }
    };

    input.click();
  }, [axiosPrivate, projectId, group_code, project_code, sendMessage]);

  // 모델(OBJ/MTL) 단일 파일 삭제: 서버에서 삭제 후 Unity에 통지
  const Delete_Model_From_StreamingAssets = useCallback(async (fileNameWithExt) => {
    try {
      console.log("Delete_Model_From_StreamingAssets", fileNameWithExt);
      console.log("typeof fileNameWithExt", typeof fileNameWithExt);
      if (!fileNameWithExt || typeof fileNameWithExt !== "string") {
        throw new Error("삭제할 파일명이 유효하지 않습니다.");
      }

      const deleteResponse = await axiosPrivate.delete("/file/delete", {
        params: {
          project_id: projectId,
          group_code,
          project_code,
          file_name: fileNameWithExt,
          upload_type: "model",
        },
      });

      console.log("모델 파일 삭제 성공:", deleteResponse.data);
      sendMessage("Canvas", "OnModelDeleteSuccess", fileNameWithExt);
    } catch (err) {
      console.error("모델 파일 삭제 실패:", err);
      sendMessage(
        "Canvas",
        "OnModelDeleteError",
        err?.message || "Delete failed"
      );
    }
  }, [axiosPrivate, projectId, group_code, project_code, sendMessage]);

  // 캔버스 크기 업데이트 함수
  const updateCanvasSize = () => {
    const unityContainer = unityContainerRef.current;
    if (unityContainer) {
      const canvas = unityContainer.querySelector("canvas");
      if (canvas) {
        const newWidth = unityContainer.clientWidth;
        const newHeight = unityContainer.clientHeight;
        const resolutionWidth = newWidth * window.devicePixelRatio;
        const resolutionHeight = newHeight * window.devicePixelRatio;

        // 유니티 WebGL 내부 해상도 업데이트
        canvas.width = resolutionWidth;
        canvas.height = resolutionHeight;
      }
    }
  };

  // 디바이스 픽셀 비율 변경 감지 및 캔버스 크기 조정
  useEffect(() => {
    const unityContainer = unityContainerRef.current;

    const handleDevicePixelRatioChange = () => {
      setDevicePixelRatio(window.devicePixelRatio);
      updateCanvasSize();
    };

    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${window.devicePixelRatio}dppx)`
    );
    mediaMatcher.addEventListener("change", handleDevicePixelRatioChange);

    updateCanvasSize();

    const observer = new ResizeObserver(() => {
      updateCanvasSize();
    });

    if (unityContainer) {
      observer.observe(unityContainer);
    }

    return () => {
      mediaMatcher.removeEventListener("change", handleDevicePixelRatioChange);
      if (unityContainer) {
        observer.unobserve(unityContainer);
      }
    };
  }, [devicePixelRatio]);

  // 우클릭, 드래그, 텍스트 선택 방지
  useEffect(() => {
    const preventDefault = (event) => {
      event.preventDefault();
    };

    const bodyElement = document.body;

    bodyElement.addEventListener("contextmenu", preventDefault);
    bodyElement.addEventListener("dragstart", preventDefault);
    bodyElement.addEventListener("selectstart", preventDefault);

    return () => {
      bodyElement.removeEventListener("contextmenu", preventDefault);
      bodyElement.removeEventListener("dragstart", preventDefault);
      bodyElement.removeEventListener("selectstart", preventDefault);
    };
  }, []);

  // 브라우저 닫기(Ctrl+W) 및 새로고침(Ctrl+R, F5) 방지
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.ctrlKey && (event.key === "w" || event.key === "r")) ||
        event.key === "F5"
      ) {
        event.preventDefault();
      }
    };

    // 페이지를 떠나기 전에 확인 메시지 표시
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ""; // Chrome에서는 returnValue를 설정해야 함
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Unity 콜백 함수들을 window 객체에 등록
  useEffect(() => {
    window.OpenFileDialog = OpenFileDialog;
    window.Upload_Image_To_StreamingAssets = Upload_Image_To_StreamingAssets;
    window.Delete_Image_To_StreamingAssets = Delete_Image_To_StreamingAssets;
    window.OpenModelFileDialog = OpenModelFileDialog;
    window.Delete_Model_From_StreamingAssets = Delete_Model_From_StreamingAssets;
    return () => {
      delete window.OpenFileDialog;
      delete window.Upload_Image_To_StreamingAssets;
      delete window.Delete_Image_To_StreamingAssets;
      delete window.OpenModelFileDialog;
      delete window.Delete_Model_From_StreamingAssets;
    };
  }, [OpenFileDialog, Upload_Image_To_StreamingAssets, Delete_Image_To_StreamingAssets, OpenModelFileDialog, Delete_Model_From_StreamingAssets]);

  return (
    <div id="le-page" style={{ width: "100%", height: "100vh" }}>
      <div
        ref={unityContainerRef}
        style={{
          width: "100%",
          height: "100vh",
          overflow: "hidden",
          position: "relative" // Added position relative for absolute positioning of loader
        }}
      >
        {/* Loading Overlay (Hidden only when both Unity and Tailwind are ready) */}
        {(!isLoaded || !isTailwindReady) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0f172a', // bg-slate-900
              color: 'white',
              fontFamily: 'monospace'
            }}
          >
            <div style={{ width: '16rem', marginBottom: '1rem', height: '0.5rem', backgroundColor: '#1e293b', borderRadius: '9999px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  backgroundColor: '#6366f1', // bg-indigo-500
                  transition: 'all 0.3s ease-out',
                  width: `${loadingProgression * 100}%`
                }}
              />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
              {!isTailwindReady ? "스타일 로드중..." : `레이아웃 에디터 로드중... ${Math.round(loadingProgression * 100)}%`}
            </p>
          </div>
        )}

        <Unity
          unityProvider={unityProvider}
          style={{ width: "100%", height: "100%" }}
          matchWebGLToCanvasSize={false} // WebGL 해상도를 캔버스 사이즈에 강제로 일치시키지 않음
        />
      </div>
    </div>
  );
}

export default LEPage;
